import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    TextField,
    Button,
    InputAdornment,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    Paper,
    FormHelperText,
    Alert,
    CircularProgress
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useStore } from '../../stores';
import { UserFormData } from '../../stores/UserStore';

interface UserRegistrationFormProps {
    selectedDriverId?: number;
    onSuccess?: () => void;
    onRoleChange?: (role: string) => void;
}

const UserRegistrationForm: React.FC<UserRegistrationFormProps> = observer(({
    selectedDriverId,
    onSuccess,
    onRoleChange
}) => {
    const { userStore } = useStore();
    const [formData, setFormData] = useState<UserFormData>({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        alias: '',
        role: 'CLIENT',
        driver_id: selectedDriverId
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [successMessage, setSuccessMessage] = useState('');


    useEffect(() => {
        if (selectedDriverId) {
            setFormData(prev => ({ ...prev, driver_id: selectedDriverId }));
        }
    }, [selectedDriverId]);

    const handleInputChange = (field: keyof UserFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));


        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: '' }));
        }
        if (userStore.error) {
            userStore.clearError();
        }
    };

    const handleRoleChange = (event: any) => {
        const role = event.target.value;
        setFormData(prev => ({ ...prev, role }));

        if (validationErrors.role) {
            setValidationErrors(prev => ({ ...prev, role: '' }));
        }


        if (onRoleChange) {
            onRoleChange(role);
        }
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleToggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuccessMessage('');


        const validation = userStore.validateUserForm(formData);

        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            return;
        }


        if (formData.role === 'DRIVER' && !selectedDriverId) {
            setValidationErrors({ driver_id: 'Debe seleccionar un conductor para el rol de DRIVER' });
            return;
        }


        const success = await userStore.createUser(formData);

        if (success) {

            setFormData({
                username: '',
                email: '',
                password: '',
                confirmPassword: '',
                alias: '',
                role: 'CLIENT',
                driver_id: undefined
            });
            setValidationErrors({});
            setSuccessMessage('Usuario creado exitosamente');


            if (onSuccess) {
                setTimeout(onSuccess, 1500);
            }
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 4,
                borderRadius: 2,
                border: '1px solid #e0e0e0'
            }}
        >
            <Typography variant="h5" component="h1" fontWeight="500" mb={1}>
                Registra un usuario
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={4}>
                Completa el formulario para registrar un nuevo usuario.
                {formData.role === 'DRIVER' && ' Recuerda que es obligatorio seleccionar un conductor de la lista.'}
            </Typography>

            <form onSubmit={handleSubmit}>
                <Box mb={3}>
                    <Typography variant="subtitle1" fontWeight="500" mb={1}>
                        Usuario *
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="Inserta el nombre de usuario"
                        value={formData.username}
                        onChange={handleInputChange('username')}
                        error={!!validationErrors.username}
                        helperText={validationErrors.username}
                        variant="outlined"
                        disabled={userStore.isSubmitting}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'white',
                            }
                        }}
                    />
                </Box>

                <Box mb={3}>
                    <Typography variant="subtitle1" fontWeight="500" mb={1}>
                        Email *
                    </Typography>
                    <TextField
                        fullWidth
                        type="email"
                        placeholder="Ingresa el email del usuario"
                        value={formData.email}
                        onChange={handleInputChange('email')}
                        error={!!validationErrors.email}
                        helperText={validationErrors.email}
                        variant="outlined"
                        disabled={userStore.isSubmitting}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'white',
                            }
                        }}
                    />
                </Box>

                <Box mb={3}>
                    <Typography variant="subtitle1" fontWeight="500" mb={1}>
                        Contraseña *
                    </Typography>
                    <TextField
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Ingresa la contraseña de la cuenta"
                        value={formData.password}
                        onChange={handleInputChange('password')}
                        error={!!validationErrors.password}
                        helperText={validationErrors.password}
                        disabled={userStore.isSubmitting}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleTogglePasswordVisibility}
                                        edge="end"
                                        disabled={userStore.isSubmitting}
                                    >
                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'white',
                            }
                        }}
                    />
                </Box>

                <Box mb={3}>
                    <Typography variant="subtitle1" fontWeight="500" mb={1}>
                        Confirmar Contraseña *
                    </Typography>
                    <TextField
                        fullWidth
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Confirma la contraseña"
                        value={formData.confirmPassword}
                        onChange={handleInputChange('confirmPassword')}
                        error={!!validationErrors.confirmPassword}
                        helperText={validationErrors.confirmPassword}
                        disabled={userStore.isSubmitting}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={handleToggleConfirmPasswordVisibility}
                                        edge="end"
                                        disabled={userStore.isSubmitting}
                                    >
                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'white',
                            }
                        }}
                    />
                </Box>

                <Box mb={3}>
                    <Typography variant="subtitle1" fontWeight="500" mb={1}>
                        Alias
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="Ingresa un alias a la cuenta"
                        value={formData.alias}
                        onChange={handleInputChange('alias')}
                        disabled={userStore.isSubmitting}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                backgroundColor: 'white',
                            }
                        }}
                    />
                </Box>

                <Box mb={5}>
                    <Typography variant="subtitle1" fontWeight="500" mb={1}>
                        Rol *
                    </Typography>
                    <FormControl fullWidth error={!!validationErrors.role}>
                        <Select
                            value={formData.role}
                            onChange={handleRoleChange}
                            disabled={userStore.isSubmitting}
                            displayEmpty
                            IconComponent={KeyboardArrowDownIcon}
                            sx={{
                                borderRadius: 2,
                                backgroundColor: 'white',
                                '& .MuiSelect-select': {
                                    p: 1.5,
                                }
                            }}
                        >
                            <MenuItem value="CLIENT">Cliente</MenuItem>
                            <MenuItem value="DRIVER">Conductor</MenuItem>
                            <MenuItem value="DISPATCHER">Despachador</MenuItem>
                            <MenuItem value="MANAGER">Manager/Supervisor</MenuItem>
                            <MenuItem value="ADMIN">Administrador</MenuItem>
                        </Select>
                        {validationErrors.role && (
                            <FormHelperText>{validationErrors.role}</FormHelperText>
                        )}
                    </FormControl>
                </Box>

                {successMessage && (
                    <Alert
                        severity="success"
                        icon={<CheckCircleIcon />}
                        sx={{ mb: 3 }}
                    >
                        {successMessage}
                    </Alert>
                )}

                {userStore.error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {userStore.error}
                    </Alert>
                )}

                {validationErrors.driver_id && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        {validationErrors.driver_id}
                    </Alert>
                )}

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={userStore.isSubmitting}
                    sx={{
                        backgroundColor: '#1A1A1A',
                        color: 'white',
                        borderRadius: 1.5,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: '#333333',
                        },
                        '&:disabled': {
                            backgroundColor: '#cccccc',
                        }
                    }}
                >
                    {userStore.isSubmitting ? (
                        <>
                            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                            Registrando...
                        </>
                    ) : (
                        'Registrar usuario'
                    )}
                </Button>
            </form>
        </Paper>
    );
});

export default UserRegistrationForm;
